import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import * as S from './UserAdsWithInfiniteScroll.styles';
import { AdsChunkSize } from './AdsWithInfiniteScroll.types';
import { AdsSection } from '@/components/AdsSection/AdsSection';
import { AdOrder, GetAdsResponse, ServiceName } from '@/api/types/ads.types';
import { GET_ADS } from '@/api/queries/ads.queries';
import { useParseQuery } from '@/hooks/useParseQuery/useParseQuery';
import { mergeAdsResults } from '@/utils/helpers';
import { useCurrentCategory } from '@/hooks/useCurrentCategory/useCurrentCategory';
import { usePreviousValue } from '@/hooks/usePreviousValue';
import { usePopState } from '@/hooks/usePopState';
import { usePlatformSource } from '@/hooks/usePlatformSource';
import { useServerDataContext } from '@/contexts';
import { DesktopFilters } from '@/components/Filters/DesktopFilters';
import { SortSelector } from '@/components/Filters/SortSelector';
import { useResponsive } from '@/hooks/useResponsive';
import { QUERY } from '@/constants/common';
import { KeywordsSource } from '@/components/SearchPanel/components/SearchSuggestions.types';
import { useBanner } from '@/hooks/useBanner';

export const UserAdsWithInfiniteScroll = () => {
  const isBrowser = typeof window !== 'undefined';
  const router = useRouter();
  const { isDesktop } = useResponsive();
  const { userLegacyId, propertyOptions, order } = useParseQuery();
  const { currentCategory: serverCurrentCategory, initialUserAds } =
    useServerDataContext();
  const { isAllCategories, currentCategory = serverCurrentCategory } =
    useCurrentCategory();
  const { isPopState, resetPopState } = usePopState();
  const source = usePlatformSource();
  const [isLoadingMore, setLoadingMore] = useState(false);
  const { inListBanner, inListDesktopBanner, bottomBanner } = useBanner({
    isDesktop,
  });

  const filters = {
    categoryId: isAllCategories ? null : currentCategory?.id,
    userLegacyId,
    propertyOptions,
  };

  const {
    data = initialUserAds,
    loading,
    fetchMore,
    refetch,
  } = useQuery<GetAdsResponse>(GET_ADS(ServiceName.Latest), {
    variables: {
      first: AdsChunkSize.Large,
      filters,
      source,
    },
    skip: !isBrowser,
  });

  const isLoading = !initialUserAds && loading;

  const resetKey = JSON.stringify({ filters });

  const prevResetKey = usePreviousValue(resetKey);

  useEffect(() => {
    if (
      prevResetKey && // to avoid resetting on the first render
      prevResetKey !== resetKey &&
      !isPopState
    ) {
      refetch();
    }
    resetPopState();
  }, [resetKey]);

  const ads = data?.ads?.nodes || [];
  const { endCursor, hasNextPage } = data?.ads?.pageInfo || {};

  function onLoadMore() {
    if (isLoading || isLoadingMore || !hasNextPage || !navigator.onLine) return;

    setLoadingMore(true);
    fetchMore({
      variables: {
        after: endCursor,
      },
      updateQuery: (prevResult, { fetchMoreResult }) =>
        mergeAdsResults(prevResult, fetchMoreResult),
    }).finally(() => setLoadingMore(false));
  }

  function handleChangeOrder(newOrder: AdOrder) {
    const { pathname, query } = router;
    delete query[QUERY.user_id];
    router.push({
      pathname,
      query: {
        ...query,
        order: newOrder.toLowerCase(),
        [QUERY.keywords_source]: KeywordsSource.Typewritten,
      },
    });
  }

  if (isDesktop) {
    return (
      <S.Wrapper>
        <DesktopFilters />
        <S.AdsListContainer>
          <SortSelector value={order} onChange={handleChangeOrder} />
          <AdsSection
            index={0}
            ads={ads}
            adsLoading={isLoading}
            hasPaddingTop
            chunkSize={AdsChunkSize.Large}
            onLoadMore={onLoadMore}
            inListDesktopBanner={inListDesktopBanner}
          />
        </S.AdsListContainer>
      </S.Wrapper>
    );
  }

  return (
    <AdsSection
      ads={ads}
      adsLoading={isLoading}
      hasPaddingTop
      chunkSize={AdsChunkSize.Large}
      onLoadMore={onLoadMore}
      bannerInList={inListBanner}
      bottomBanner={bottomBanner}
      isBannerInListAtTheEnd
      isBannerInListAfterThreeRows
    />
  );
};
